import { LocationIcon } from '@velocity/icons/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../layout';
import { setNotification } from '../../../redux/cache-slice';
import {
  fetchDealersList,
  setSelectedDealer,
  updateCustomerData,
} from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { APIPaths, Dealer } from '../../../types/instant-lease-api';
import { haversineDistance } from '../../../utils/geo';
import { errorLogging } from '../../../utils/logging';
import Navigation from '../../../utils/navigation';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';

// The dealer-search API can return multiple dealers that share the same `id`
// (e.g. the `smart` brand returns every dealer with id "3"). Build a composite
// identity so selection and React keys stay unique per physical location.
const getDealerKey = (
  dealer: Pick<Dealer, 'id' | 'lat' | 'lng' | 'street'>,
): string =>
  `${dealer.id}-${dealer.lat ?? ''}-${dealer.lng ?? ''}-${dealer.street}`;

const DealerSelection: React.FC = () => {
  const { activeApplication, dealers, selectedDealer, carData } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  const { accessToken } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextRoute } = new Navigation();

  const [activeTab, setActiveTab] = useState<'suggestions' | 'search'>(
    'suggestions',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingDealers, setFetchingDealers] = useState(false);
  const [geocodingResolved, setGeocodingResolved] = useState(false);
  const [customerCoords, setCustomerCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Get brand from car data
  const brand = useMemo(() => carData?.brand || '', [carData]);

  const dealerList = useMemo(() => carData?.dealerList || '', [carData]);
  // get tenant from car data
  const tenant = useMemo(() => carData?.tenant || '', [carData]);

  // Fetch dealers on mount
  useEffect(() => {
    if (dealerList && tenant) {
      setFetchingDealers(true);
      dispatch(fetchDealersList({ dealerList, tenant })).finally(() => {
        setFetchingDealers(false);
      });
    }
  }, [brand, tenant, dispatch, dealerList]);

  const getCustomerAddress = useCallback(() => {
    const customer = activeApplication?.customer;
    const privateAddress =
      customer?.private?.person?.contact_details?.addresses?.[0];
    const companyAddress =
      customer?.company?.contact_details?.addresses?.[0] ||
      customer?.individual?.contact_details?.addresses?.[0];

    const address = privateAddress || companyAddress;
    if (!address) return null;

    return {
      street: address.street_line || '',
      postalCode: address.postal_code || '',
      zipCode: address.postal_code || '',
      city: address.locality || '',
      country: address.country_code || '',
    };
  }, [activeApplication]);

  // Geocode customer address to get coordinates Legacy
  // TODO : Switch to the new Geocode API once it's available
  useEffect(() => {
    const address = getCustomerAddress();
    if (!address) {
      setGeocodingResolved(true);
      return;
    }
    const addressString = `${address.street} ${address.postalCode} ${address.city}`;

    fetch(`/gc-legacy?address=${encodeURIComponent(addressString)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Geocoding failed with HTTP status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data?.status !== 'OK' || !data.results?.[0]) {
          throw new Error(
            `Geocoding failed with status ${data?.status ?? 'UNKNOWN'}`,
          );
        }
        const location = data.results[0].geometry?.location;
        if (
          !Number.isFinite(location?.lat) ||
          !Number.isFinite(location?.lng) ||
          Math.abs(location.lat) > 90 ||
          Math.abs(location.lng) > 180
        ) {
          throw new Error('Geocoding returned invalid coordinates');
        }
        setCustomerCoords({ lat: location.lat, lng: location.lng });
      })
      .catch((error) => {
        setCustomerCoords(null);
        console.error('Geocoding failed', error);
        setActiveTab('search');
        // show snackbar or some user notification about the geocoding failure
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: false,
            notificationHasWarning: true,
            notificationTitle: 'Standort konnte nicht ermittelt werden',
            notificationBody:
              'Deine Adresse konnte nicht geortet werden. Bitte wähle einen Händler aus der Liste aus.',
          }),
        );
        errorLogging(
          error instanceof Error ? error : new Error('Geocoding failed'),
        );
      })
      .finally(() => {
        setGeocodingResolved(true);
      });
  }, [getCustomerAddress, dispatch]);

  // Geocode customer address to get coordinates
  /*
  useEffect(() => {
    const address = getCustomerAddress();
    if (!address) {
      setGeocodingResolved(true);
      return;
    }

    const params = new URLSearchParams({
      street: address.street || '',
      zipCode: address.postalCode || '',
      city: address.city || '',
      country: address.country || '',
    });



    fetch(`/geocode?${params.toString()}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Geocoding failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setCustomerCoords(data);
        }
      })
      .catch((error) => {
        console.error('Geocoding failed');
        setActiveTab('search');
        // show snackbar or some user notification about the geocoding failure
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: false,
            notificationHasWarning: true,
            notificationTitle: 'Standort konnte nicht ermittelt werden',
            notificationBody:
              'Deine Adresse konnte nicht geortet werden. Bitte wähle einen Händler aus der Liste aus.',
          }),
        );
        errorLogging(
          error instanceof Error ? error : new Error('Geocoding failed'),
        );
      })
      .finally(() => {
        setGeocodingResolved(true);
      });
  }, [getCustomerAddress, dispatch]);
*/

  // Calculate distances and sort dealers
  const dealersWithDistance = useMemo(() => {
    if (!dealers) return [];
    if (!Array.isArray(dealers)) return [];

    return dealers
      .map((dealer) => {
        let distance: number | null = null;
        if (customerCoords && dealer.lat && dealer.lng) {
          distance = haversineDistance(
            customerCoords.lat,
            customerCoords.lng,
            dealer.lat,
            dealer.lng,
          );
        }
        return { ...dealer, distance };
      })
      .sort((a, b) => {
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
  }, [dealers, customerCoords]);

  // Filtered dealers for search
  const filteredDealers = useMemo(() => {
    // Show only dealers within 50 km on the suggestions tab, ensuring at least one is displayed regardless of distance.
    if (activeTab === 'suggestions') {
      const nearby = dealersWithDistance.filter(
        (d) => d.distance !== null && d.distance <= 50,
      );
      return nearby.length > 0 ? nearby : dealersWithDistance.slice(0, 1);
    }
    if (!searchQuery.trim()) return dealersWithDistance;
    const query = searchQuery.toLowerCase();
    return dealersWithDistance.filter(
      (d) =>
        d.name1.toLowerCase().includes(query) ||
        d.street.toLowerCase().includes(query) ||
        d.city.toLowerCase().includes(query) ||
        d.zip_code.includes(query),
    );
  }, [activeTab, dealersWithDistance, searchQuery]);

  // Auto-select nearest dealer if none selected
  useEffect(() => {
    if (!geocodingResolved) return;
    if (!selectedDealer && dealersWithDistance.length > 0) {
      const existingDealer = activeApplication?.offer?.dealer;
      const nearestDealer = dealersWithDistance.find(
        (dealer) => dealer.distance !== null,
      );
      if (existingDealer) {
        const match = dealersWithDistance.find(
          (d) =>
            d.id === existingDealer.id && d.street === existingDealer.street,
        );
        const dealerToSelect = match || nearestDealer;
        if (dealerToSelect) {
          dispatch(setSelectedDealer(dealerToSelect));
        }
      } else if (nearestDealer) {
        dispatch(setSelectedDealer(nearestDealer));
      }
    }
  }, [
    geocodingResolved,
    dealersWithDistance,
    selectedDealer,
    activeApplication,
    dispatch,
  ]);

  const handleSelect = (dealer: Dealer) => {
    dispatch(setSelectedDealer(dealer));
  };

  const handleSubmit = () => {
    if (!activeApplication) return;
    if (!selectedDealer) {
      navigate(nextRoute());
      return;
    }
    setLoading(true);

    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication.uuid,
        path: APIPaths.UPDATE_DEALER,
        inputValue: {
          dealer: {
            address_line1: selectedDealer.name1 || '',
            address_line2: selectedDealer.name2 || '',
            brand: selectedDealer.brand,
            city: selectedDealer.city,
            dealer_id: selectedDealer.id,
            dealer_name: selectedDealer.name1 || '',
            email_address: selectedDealer.email || '',
            lease_man_delivery_id: selectedDealer.lease_man_delivery_id || '',
            name1: selectedDealer.name1 || '',
            name2: selectedDealer.name2 || '',
            origin: 'DealerSearchService - Customer Selected',
            partner_id: selectedDealer.partner_id || '',
            partner: selectedDealer.partner, // e.g., tenant / partner 'ald_marktplatz'
            phone: selectedDealer.phone || '',
            postal_code: selectedDealer.zip_code,
            street: selectedDealer.street,
          },
        },
      }),
    )
      .then(() => {
        setLoading(false);
        navigate(nextRoute());
      })
      .catch(() => {
        setLoading(false);
        navigate(nextRoute());
      });
  };

  const openMap = (dealer: Dealer) => {
    const address = encodeURIComponent(
      `${dealer.street}, ${dealer.zip_code} ${dealer.city}`,
    );
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${address}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <Layout heading='Wähle deinen Händler' stepStatus>
      <div className='template-body'>
        <p className='text-[15px] font-light text-(--vel-color-secondary-400)'>
          Damit du dein Fahrzeug schnell in Empfang nehmen kannst, schlagen wir
          dir den nächstgelegenen Händler vor. Du kannst aber auch in der Liste
          nach einem anderen passenden Händler suchen.
        </p>

        <div className='relative mt-12! flex h-14 rounded-lg bg-(--vel-color-neutral-200)'>
          {/* Sliding highlight */}
          <div
            className='absolute top-2 bottom-2 rounded-md bg-(--vel-color-primary-400) transition-all duration-300 ease-in-out'
            style={{
              left: activeTab === 'suggestions' ? '8px' : '50%',
              right: activeTab === 'search' ? '8px' : '50%',
            }}
          />
          <button
            type='button'
            className={`relative flex-1! rounded-lg text-sm! font-semibold! focus-visible:shadow-[0_0_0_2px_#0065ff]! ${
              activeTab === 'suggestions'
                ? 'text-white'
                : 'text-(--vel-color-secondary-400)!'
            }`}
            onClick={() => setActiveTab('suggestions')}
          >
            Unsere Vorschläge
          </button>
          <button
            type='button'
            className={`relative flex-1! rounded-lg text-sm! font-semibold! focus-visible:shadow-[0_0_0_2px_#0065ff]! ${
              activeTab === 'search'
                ? 'text-white'
                : 'text-(--vel-color-secondary-400)!'
            }`}
            onClick={() => setActiveTab('search')}
          >
            Händler suchen
          </button>
        </div>

        {/* Search Input (Conditional) */}
        {activeTab === 'search' && (
          <div className='mt-12! mb-4!'>
            <h2 className='mb-0.5! text-lg font-semibold'>
              Meinen Händler finden
            </h2>
            <p className='mb-6! text-base'>
              <span className='font-semibold'>{filteredDealers.length} </span>
              Händler gefunden
            </p>
            <input
              type='text'
              placeholder='Name oder Anschrift eingeben...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='-mb-3! w-full! rounded-lg! border! border-solid! border-(--vel-color-neutral-300)! px-4! py-3! text-sm! outline-none! focus:border-(--vel-color-primary-400)!'
            />
          </div>
        )}

        {/* Dealer List */}
        <div className='mt-13! flex! flex-col! gap-2!'>
          {filteredDealers.map((dealer) => {
            const isSelected =
              !!selectedDealer &&
              getDealerKey(selectedDealer) === getDealerKey(dealer);
            return (
              <button
                key={getDealerKey(dealer)}
                type='button'
                onClick={() => handleSelect(dealer)}
                className={`flex! w-full! items-center! gap-3! rounded-lg! border-0! p-4! text-left! transition-colors! outline-none! focus-visible:shadow-[0_0_0_2px_#0065ff]! ${
                  isSelected
                    ? 'bg-(--vel-color-primary-400)! text-white!'
                    : 'bg-(--vel-color-semantic-info-low)!'
                }`}
              >
                {/* Radio indicator */}
                <div className='shrink-0!'>
                  <div
                    className={`flex! h-5! w-5! items-center! justify-center! rounded-full! border-2! border-solid! ${
                      isSelected
                        ? 'border-white!'
                        : 'border-(--vel-color-primary-400)!'
                    }`}
                  >
                    {isSelected && (
                      <div className='h-2.5! w-2.5! rounded-full! bg-white!' />
                    )}
                  </div>
                </div>

                {/* Dealer info */}
                <div className='min-w-0! flex-1!'>
                  <p
                    className={`text-sm! font-semibold! ${isSelected ? 'text-white!' : 'text-(--vel-color-secondary-400)!'}`}
                  >
                    {dealer.name1}
                  </p>
                  <p
                    className={`text-sm! font-light ${isSelected ? 'text-white!' : 'text-(--vel-color-secondary-400)!'}`}
                  >
                    {dealer.street} – {dealer.zip_code} {dealer.city}
                  </p>
                </div>

                {/* Distance & map link */}
                {dealer.distance !== null && (
                  <div className='flex! shrink-0! flex-col! items-end! gap-0.5!'>
                    <div className='flex! items-center! gap-1!'>
                      <span
                        className={
                          isSelected
                            ? 'text-white!'
                            : 'text-(--vel-color-primary-400)!'
                        }
                      >
                        <LocationIcon size='s' />
                      </span>
                      <span
                        className={`text-sm! ${isSelected ? 'text-white!' : 'text-(--vel-color-secondary-400)!'}`}
                      >
                        Entfernung:{' '}
                        <strong>
                          {dealer.distance.toLocaleString('de-DE', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}{' '}
                          km
                        </strong>
                      </span>
                    </div>
                    <span
                      role='link'
                      tabIndex={0}
                      className={`cursor-pointer! rounded-sm! text-sm! underline underline-offset-2! focus-visible:shadow-[0_0_0_2px_#0065ff]! ${isSelected ? 'text-white!' : 'text-(--vel-color-primary-400)!'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openMap(dealer);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          openMap(dealer);
                        }
                      }}
                    >
                      Auf Karte anzeigen
                    </span>
                  </div>
                )}
              </button>
            );
          })}

          {filteredDealers.length === 0 && !dealers?.length && (
            <p className='py-8! text-center! text-sm! text-(--vel-color-neutral-500)!'>
              {fetchingDealers
                ? 'Händler werden geladen...'
                : 'Aktuell sind keine Händler verfügbar. Du kannst trotzdem fortfahren.'}
            </p>
          )}
          {activeTab === 'search' &&
            filteredDealers.length === 0 &&
            !!dealers?.length && (
              <p className='py-8! text-center! text-sm! text-(--vel-color-neutral-500)!'>
                Keine Händler gefunden.
              </p>
            )}
        </div>

        {/* Submit Button */}
        <ButtonGroup
          type='back-next'
          className='back-next'
          buttonOneProps={{
            name: 'weiter',
            type: 'submit',
            theme: 'primary',
            loading,
            disabled: fetchingDealers || (!!dealers?.length && !selectedDealer),
            onClick: handleSubmit,
            children: 'weiter',
          }}
        />
      </div>
    </Layout>
  );
};

export default DealerSelection;
